import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Badge } from "../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog"
import { Label } from "../components/ui/label"
import { Textarea } from "../components/ui/textarea"
import { PageLayout } from "../components/page-layout"
import { MoonLoader } from "../components/ui/moon-loader"
import { useToast } from "../hooks/use-toast"
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/auth-context'
import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Users,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  MapPin,
  RefreshCw,
  Settings,
  Bell,
  ArrowRightLeft,
  Calendar,
  DollarSign,
  Activity,
  PieChart,
  Check,
  X,
  ArrowRight,
  Calculator,
  Shield,
  AlertCircle,
  Info,
  User,
  Star,
  Phone,
  Mail,
  UserCheck,
  Clock,
  Thermometer,
  FileText,
  Download,
  Upload,
  MoreHorizontal,
  Trash2,
  Copy,
  Save,
  Send,
  Play,
  Pause,
  RotateCcw,
  Target,
  Zap,
  Brain,
  Building2,
  CreditCard,
  Receipt,
  Wallet,
  Package,
  BookOpen,
  HelpCircle,
  Home,
  ShoppingCart,
  ShoppingBag,
  Banknote,
  FileText as FileTextIcon,
  MessageSquare,
  FolderOpen,
  Building,
  Settings as SettingsIcon,
  HelpCircle as HelpCircleIcon,
  Brain as BrainIcon,
  Zap as ZapIcon,
  BarChart3 as BarChart3Icon,
  Activity as ActivityIcon,
  Calculator as CalculatorIcon,
  Loader2,
  Receipt as ReceiptIcon
} from "lucide-react"
import { payrollApi } from '@/lib/api/payroll'
import { getCompanyId } from '@/lib/config'
import { EmployeeManagement } from '@/components/payroll/employee-management'
import { PayrollManagement } from '@/components/payroll/payroll-management'
import { TimeTrackingManagement } from '@/components/payroll/time-tracking-management'
import { PayrollReports } from '@/components/payroll/payroll-reports'
import { EmployeeModal, PayrollPeriodModal } from '@/components/payroll/payroll-modals'
import { TranslatedText } from '../components/translated-text'
import { useTranslation } from '../hooks/useTranslation'

export default function PayrollPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTab, setSelectedTab] = useState('employees')
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false)
  const [isPayrollModalOpen, setIsPayrollModalOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [selectedPeriod, setSelectedPeriod] = useState(null)
  const [filterDepartment, setFilterDepartment] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  
  const { toast } = useToast()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { tSync } = useTranslation()
  const queryClient = useQueryClient()
  const companyId = getCompanyId()

  // Employee queries - fetch all employees without pagination
  const { data: employees, isLoading: employeesLoading, error: employeesError } = useQuery({
    queryKey: ['payroll-employees', companyId, searchTerm, filterDepartment, filterStatus],
    queryFn: () => payrollApi.getEmployees({
      search: searchTerm,
      department: filterDepartment,
      status: filterStatus,
      page: 1,
      limit: 1000 // Fetch all employees
    }),
    enabled: !!companyId
  })

  // Payroll periods queries
  const { data: periods, isLoading: periodsLoading } = useQuery({
    queryKey: ['payroll-periods', companyId],
    queryFn: () => payrollApi.getPayrollPeriods(),
    enabled: !!companyId
  })


  // Dashboard data
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['payroll-dashboard', companyId],
    queryFn: () => payrollApi.getDashboardData(),
    enabled: !!companyId
  })

  const handleCreateEmployee = async (employeeData) => {
    try {
      await payrollApi.createEmployee(employeeData)
      queryClient.invalidateQueries({ queryKey: ['payroll-employees'] })
      toast({
        title: tSync("Success"),
        description: tSync("Employee created successfully"),
      })
      setIsEmployeeModalOpen(false)
    } catch (error: any) {
      console.error('Error creating employee:', error)
      
      // Handle specific error types from API
      if (error.response?.data?.error === 'duplicate_email') {
        toast({
          title: tSync("Duplicate Email"),
          description: error.response.data.message || tSync("An employee with this email already exists. Please use a different email or update the existing employee."),
          variant: "destructive",
        })
      } else if (error.response?.data?.error === 'database_error') {
        toast({
          title: tSync("Database Error"),
          description: error.response.data.message || tSync("A database error occurred while creating the employee."),
          variant: "destructive",
        })
      } else if (error.response?.data?.message) {
        toast({
          title: tSync("Error"),
          description: error.response.data.message,
          variant: "destructive",
        })
      } else if (error.message) {
        toast({
          title: tSync("Error"),
          description: error.message,
          variant: "destructive",
        })
      } else {
        toast({
          title: tSync("Error"),
          description: tSync("Failed to create employee. Please try again."),
          variant: "destructive",
        })
      }
    }
  }

  const handleUpdateEmployee = async (employeeData) => {
    try {
      const result = await payrollApi.updateEmployee(selectedEmployee.id, employeeData)
      
      queryClient.invalidateQueries({ queryKey: ['payroll-employees'] })
      toast({
        title: tSync("Success"),
        description: tSync("Employee updated successfully"),
      })
      setIsEmployeeModalOpen(false)
      setSelectedEmployee(null)
    } catch (error) {
      console.error('Update error:', error)
      toast({
        title: tSync("Error"),
        description: tSync("Failed to update employee"),
        variant: "destructive",
      })
    }
  }

  const handleProcessPayroll = async (periodId) => {
    try {
      await payrollApi.processPayroll(periodId)
      queryClient.invalidateQueries({ queryKey: ['payroll-periods'] })
      queryClient.invalidateQueries({ queryKey: ['payroll-dashboard'] })
      toast({
        title: tSync("Success"),
        description: tSync("Payroll processed successfully"),
      })
    } catch (error) {
      toast({
        title: tSync("Error"),
        description: tSync("Failed to process payroll"),
        variant: "destructive",
      })
    }
  }


  if (employeesLoading || periodsLoading || dashboardLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <MoonLoader />
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout showBreadcrumbs={false}>
      <div className="space-y-6 -mt-4 pb-24 pr-12">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight"><TranslatedText text="Payroll Management" /></h1>
            <p className="text-muted-foreground">
              <TranslatedText text="Manage employees, process payroll, and track time" />
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => setIsEmployeeModalOpen(true)}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span><TranslatedText text="Add Employee" /></span>
            </Button>
            <Button
              onClick={() => setIsPayrollModalOpen(true)}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <Calendar className="h-4 w-4" />
              <span><TranslatedText text="New Pay Period" /></span>
            </Button>
          </div>
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Total Employees" /></CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData?.totalEmployees || 0}</div>
              <p className="text-xs text-muted-foreground">
                {dashboardData?.activeEmployees || 0} <TranslatedText text="active" />
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Pending Leave Requests" /></CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData?.pendingLeaveRequests || 0}</div>
              <p className="text-xs text-muted-foreground">
                <TranslatedText text="Requires approval" />
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Recent Pay Periods" /></CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData?.recentPeriods?.length || 0}</div>
              <p className="text-xs text-muted-foreground">
                <TranslatedText text="Last 6 periods" />
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Time Entries" /></CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData?.recentTimeEntries?.length || 0}</div>
              <p className="text-xs text-muted-foreground">
                <TranslatedText text="Last 7 days" />
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="employees"><TranslatedText text="Employees" /></TabsTrigger>
            <TabsTrigger value="payroll"><TranslatedText text="Payroll" /></TabsTrigger>
            {/* Time Tracking tab hidden for now */}
            {/* <TabsTrigger value="time-tracking"><TranslatedText text="Time Tracking" /></TabsTrigger> */}
            <TabsTrigger value="reports"><TranslatedText text="Reports" /></TabsTrigger>
          </TabsList>

          {/* Employees Tab */}
          <TabsContent value="employees" className="space-y-4">
            <EmployeeManagement 
              employees={employees?.employees || []}
              totalEmployees={employees?.total || 0}
              onEditEmployee={(employee) => {
                setSelectedEmployee(employee)
                setIsEmployeeModalOpen(true)
              }}
              onCreateEmployee={handleCreateEmployee}
              onOpenCreateModal={() => setIsEmployeeModalOpen(true)}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterDepartment={filterDepartment}
              setFilterDepartment={setFilterDepartment}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
            />
          </TabsContent>

          {/* Payroll Tab */}
          <TabsContent value="payroll" className="space-y-4">
            <PayrollManagement 
              periods={periods?.payrollPeriods || []}
              onProcessPayroll={handleProcessPayroll}
              onCreatePeriod={() => setIsPayrollModalOpen(true)}
            />
          </TabsContent>

          {/* Time Tracking Tab - Hidden for now */}
          {/* <TabsContent value="time-tracking" className="space-y-4">
            <TimeTrackingManagement 
              employees={employees?.employees || []}
            />
          </TabsContent> */}

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <PayrollReports />
          </TabsContent>
        </Tabs>

        {/* Modals */}
        <EmployeeModal 
          isOpen={isEmployeeModalOpen}
          onClose={() => {
            setIsEmployeeModalOpen(false)
            setSelectedEmployee(null)
          }}
          onSubmit={selectedEmployee ? handleUpdateEmployee : handleCreateEmployee}
          employee={selectedEmployee}
        />

        <PayrollPeriodModal 
          isOpen={isPayrollModalOpen}
          onClose={() => setIsPayrollModalOpen(false)}
          onSubmit={async (data) => {
            try {
              await payrollApi.createPayrollPeriod(data)
              queryClient.invalidateQueries({ queryKey: ['payroll-periods'] })
              toast({
                title: tSync("Success"),
                description: tSync("Payroll period created successfully"),
              })
              setIsPayrollModalOpen(false)
            } catch (error) {
              console.error('Error creating payroll period:', error)
              
              // Handle specific error types
              if (error.response?.data?.error === 'duplicate_period') {
                toast({
                  title: tSync("Duplicate Period"),
                  description: error.response.data.message || tSync("A payroll period with the same dates already exists."),
                  variant: "destructive",
                })
              } else if (error.response?.data?.error === 'duplicate_entry') {
                toast({
                  title: tSync("Duplicate Entry"),
                  description: error.response.data.message || tSync("A payroll period with these details already exists."),
                  variant: "destructive",
                })
              } else {
                toast({
                  title: tSync("Error"),
                  description: tSync("Failed to create payroll period"),
                  variant: "destructive",
                })
              }
            }
          }}
        />
      </div>
    </PageLayout>
  )
}
